import { Anchor, Group, Skeleton, Text } from '@mantine/core';
import { IconChartBar, IconCoin } from '@tabler/icons-react';
import { Link } from 'react-router-dom';
import { BILLING_ENABLED } from '@constants/features';
import { useTheme } from '@contexts/ThemeContext';
import { useLanguage } from '@contexts/LanguageContext';
import useResolveCreditAccount from '@hooks/credit/use-resolve-credit-account';
import useAccountBalances from '@hooks/subscription/use-account-balances';
import useMonthUsage from '@hooks/subscription/use-month-usage';
import { type CreditStripVariant, monthUsageChips } from './credits-strip';

/** Billable products shown on Doc Pabean (PIB/PEB + Manifest house). */
const DOC_PABEAN_CREDIT_PRODUCTS = [
  { code: 'ceisa_submission', label: 'PIB / PEB' },
  { code: 'nvocc_manifest_submission', label: 'Manifest' },
] as const;

/**
 * Credits strip for Doc Pabean and Doc Manifes.
 *
 * - A PPJK on a plan JARVIS invoices monthly (`overageBilled`) sees what it
 *   used this calendar month: PIB and PEB on Dok Pabean, House BL on Dok
 *   Manifes. Plain counts, with nothing about a quota or going past one.
 * - A prepaid PPJK with credit enforcement on (`isCreditActive`) sees its
 *   remaining credits, as before.
 */
export default function DocPabeanCreditsBanner({ variant = 'pabean' }: { variant?: CreditStripVariant }) {
  const { colors: themeColors } = useTheme();
  const { t } = useLanguage();
  const { accountCode, loading: resolveLoading, isPaymentActive } = useResolveCreditAccount();
  const {
    data: balances,
    loading: balancesLoading,
    error: balancesError,
    isCreditActive,
    overageBilled,
  } = useAccountBalances(isPaymentActive ? accountCode : undefined);
  const {
    data: monthUsage,
    loading: usageLoading,
    error: usageError,
  } = useMonthUsage(overageBilled && accountCode ? accountCode : undefined);

  // Wait for resolve + balances (+ the month's usage) so nothing flashes.
  if (resolveLoading || (Boolean(accountCode) && (balancesLoading || (overageBilled && usageLoading)))) {
    return (
      <Group gap="md" mb="md" wrap="wrap">
        <Skeleton height={36} width={160} radius="md" />
        <Skeleton height={36} width={160} radius="md" />
      </Group>
    );
  }

  // Gate: linked account alone is not enough — hide when credit is inactive.
  if (!isPaymentActive || !accountCode || (!isCreditActive && !overageBilled)) {
    return null;
  }

  const balanceFor = (productCode: string): number | null => {
    const row = balances.find((b) => b.product?.code === productCode);
    return row ? row.totalCredits : null;
  };
  const chips = overageBilled
    ? monthUsageChips(monthUsage, variant)
    : DOC_PABEAN_CREDIT_PRODUCTS.map(({ code, label }) => ({ key: code, label, value: balanceFor(code) ?? '—' }));
  const error = overageBilled ? usageError : balancesError;

  return (
    <Group
      data-tour="docpabean-credits"
      justify="space-between"
      align="center"
      wrap="wrap"
      mb="md"
      gap="sm"
      p="sm"
      style={{
        backgroundColor: `${themeColors.primary}08`,
        border: `1px solid ${themeColors.border}`,
        borderRadius: 8,
      }}
    >
      <Group gap="md" wrap="wrap">
        <Group gap={6}>
          {overageBilled ? (
            <IconChartBar size={16} style={{ color: themeColors.primary }} />
          ) : (
            <IconCoin size={16} style={{ color: themeColors.primary }} />
          )}
          <Text size="sm" fw={600} style={{ color: themeColors.text }}>
            {t(overageBilled ? 'docpabean.usageThisMonth' : 'docpabean.remainingCredits')}
          </Text>
        </Group>

        {chips.map(({ key, label, value }) => (
          <Group
            key={key}
            gap={6}
            px="sm"
            py={4}
            style={{
              backgroundColor: themeColors.card,
              border: `1px solid ${themeColors.border}`,
              borderRadius: 6,
            }}
          >
            <Text size="xs" style={{ color: themeColors.textSecondary }}>
              {label}
            </Text>
            <Text size="sm" fw={700} style={{ color: themeColors.text }}>
              {value}
            </Text>
          </Group>
        ))}

        {error && (
          <Text size="xs" c="red">
            {t('docpabean.errLoadBalance')}
          </Text>
        )}
      </Group>

      {BILLING_ENABLED && !overageBilled && (
        <Anchor component={Link} to="/subscription" size="xs" style={{ color: themeColors.primary }}>
          {t('docpabean.viewSubscription')}
        </Anchor>
      )}
    </Group>
  );
}
